<template>
  <div
    ref="rootRef"
    :class="[cls.root, mergedSize === 'small' ? `${cls.root}--small` : '', $attrs.class]"
    :style="[cardStyle, $attrs.style]"
    @click="handleClick"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <!-- title -->
    <StatisticCardTitle :header="resolvedHeader" :size="mergedSize" />

    <!-- chart placement === 'right': 数值 + 图表并排 -->
    <template v-if="resolvedChart && resolvedChart.placement === 'right'">
      <div :style="rightLayoutStyle">
        <div :style="rightLeftColStyle">
          <StatisticCardValue :value="item.value" :size="mergedSize" :number-scroll="mergedNumberScroll" />
          <StatisticCardSubIndicators :items="item.subIndicators" :size="mergedSize" :reverse-color="effectiveReverseColor" :layout="mergedSubIndicatorsLayout" />
        </div>
        <StatisticCardChart :chart="resolvedChart" :size="mergedSize">
          <component :is="resolvedChart.content" v-if="typeof resolvedChart.content === 'object'" />
          <template v-else>{{ resolvedChart.content }}</template>
        </StatisticCardChart>
      </div>
    </template>

    <!-- 默认布局：纵向排列 -->
    <template v-else>
      <StatisticCardValue :value="item.value" :size="mergedSize" :number-scroll="mergedNumberScroll" />
      <StatisticCardSubIndicators :items="item.subIndicators" :size="mergedSize" :reverse-color="effectiveReverseColor" :layout="mergedSubIndicatorsLayout" />
      <StatisticCardChart v-if="resolvedChart && resolvedChart.placement === 'bottom'" :chart="resolvedChart" :size="mergedSize">
        <component :is="resolvedChart.content" v-if="typeof resolvedChart.content === 'object'" />
        <template v-else>{{ resolvedChart.content }}</template>
      </StatisticCardChart>
    </template>

    <!-- extra -->
    <StatisticCardExtra :extra="resolvedExtra">
      <component :is="resolvedExtra?.content" v-if="resolvedExtra && typeof resolvedExtra.content === 'object'" />
      <template v-else-if="resolvedExtra">{{ resolvedExtra.content }}</template>
    </StatisticCardExtra>

    <!-- selection checkbox -->
    <StatisticCardSelection :control-type="mergedControlType" :checked="mergedChecked" :disabled="item.disabled" />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, inject, type CSSProperties, type PropType } from 'vue'
import { useNamespace } from '@jdesign-vue/hooks'
import type {
  StatisticCardItemData,
  StatisticCardSize,
  StatisticCardControlType,
  StatisticCardResolvedHeader,
  StatisticCardHeader,
  StatisticCardTooltipRender,
} from '../types'
import {
  CARD_INTERNAL_GAP,
  CARD_RIGHT_PLACEMENT_GAP,
  CARD_HEIGHT,
  CARD_MIN_WIDTH,
  createColorVars,
  STATISTIC_CARD_CLASSES,
} from '../constants'
import {
  statisticGroupKey,
  statisticCardDataKey,
  statisticCardAppearanceKey,
  statisticCardSelectionKey,
} from '../tokens'
import StatisticCardTitle from './statistic-card-title.vue'
import StatisticCardValue from './statistic-card-value.vue'
import StatisticCardSubIndicators from './statistic-card-sub-indicators.vue'
import StatisticCardChart from './statistic-card-chart.vue'
import StatisticCardExtra from './statistic-card-extra.vue'
import StatisticCardSelection from './statistic-card-selection.vue'

function isTooltipRender(tooltip: StatisticCardHeader['tooltip']): tooltip is StatisticCardTooltipRender {
  return typeof tooltip === 'function'
}

export default defineComponent({
  name: 'JdStatisticCard',
  components: {
    StatisticCardTitle,
    StatisticCardValue,
    StatisticCardSubIndicators,
    StatisticCardChart,
    StatisticCardExtra,
    StatisticCardSelection,
  },
  inheritAttrs: false,
  props: {
    item: { type: Object as PropType<StatisticCardItemData>, required: true },
    size: { type: String as PropType<StatisticCardSize>, default: undefined },
    reverseColor: { type: Boolean, default: undefined },
    border: { type: Boolean, default: undefined },
    equalHeight: { type: Boolean, default: undefined },
    numberScroll: { type: Boolean, default: undefined },
    subIndicatorsLayout: { type: String as PropType<'horizontal' | 'vertical'>, default: undefined },
    checked: { type: Boolean, default: undefined },
    controlType: { type: String as PropType<StatisticCardControlType>, default: undefined },
    onClick: { type: Function as PropType<(item: StatisticCardItemData) => void>, default: undefined },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const cls = STATISTIC_CARD_CLASSES
    const ns = useNamespace('statistic-card')
    const colorVars = createColorVars(ns.cssVarName)
    const rootRef = ref<HTMLDivElement | null>(null)
    const hovered = ref(false)

    // --- inject contexts ---
    const groupCtx = inject(statisticGroupKey, { inGroup: false })
    const dataCtx = inject(statisticCardDataKey, undefined)
    const appearanceCtx = inject(statisticCardAppearanceKey, undefined)
    const selectionCtx = inject(statisticCardSelectionKey, undefined)

    // --- merged props (本组件 props 优先 > appearance 上下文 > 默认值) ---
    const appearance = computed(() => appearanceCtx?.appearance.value)
    const mergedSize = computed<StatisticCardSize>(() => props.size ?? appearance.value?.size ?? 'large')
    const mergedBorder = computed(() => props.border ?? appearance.value?.border ?? true)
    const mergedNumberScroll = computed(() => props.numberScroll ?? appearance.value?.numberScroll ?? false)
    const mergedSubIndicatorsLayout = computed(() => props.subIndicatorsLayout ?? appearance.value?.subIndicatorsLayout ?? 'vertical')
    const effectiveReverseColor = computed(() => props.item.reverseColor ?? props.reverseColor ?? appearance.value?.reverseColor ?? false)
    // 单卡直接使用时默认不等高(false);经容器(list/formula)使用时,容器会显式下发算好的 equalHeight,此兜底不触发
    const mergedEqualHeight = computed(() => props.equalHeight ?? appearance.value?.equalHeight ?? false)

    // --- selection ---
    const mergedControlType = computed<StatisticCardControlType>(() => props.controlType ?? selectionCtx?.controlType.value ?? null)
    const mergedChecked = computed(() => props.checked ?? (selectionCtx ? selectionCtx.isChecked(props.item.id) : false))
    const selectionDisabled = computed(() => selectionCtx ? selectionCtx.isLocked(props.item.id) : false)
    const interactive = computed(() => Boolean(props.onClick || mergedControlType.value))

    // --- resolve header (tooltip 函数调用) ---
    const statisticCardData = computed(() => (dataCtx ? dataCtx.data.value : [props.item]))
    const resolvedHeader = computed<StatisticCardResolvedHeader>(() => {
      const h = props.item.header
      return {
        ...h,
        tooltip: isTooltipRender(h.tooltip) ? h.tooltip(props.item.id, props.item, statisticCardData.value) : h.tooltip,
      }
    })

    // --- resolve chart ---
    const resolvedChart = computed(() => {
      if (!props.item.chart) return undefined
      return { content: props.item.chart.content, placement: props.item.chart.placement ?? 'right' }
    })

    // --- resolve extra ---
    const resolvedExtra = computed(() => {
      const raw = props.item.extra
      if (!raw) return undefined
      if (typeof raw === 'string') return { content: raw, divided: true }
      if (typeof raw === 'object' && raw !== null && 'content' in raw) {
        const obj = raw as { content: unknown; divided?: boolean }
        return { content: obj.content, divided: obj.divided ?? false }
      }
      return { content: raw, divided: true }
    })

    // --- card container style ---
    const cardStyle = computed<CSSProperties>(() => {
      const disabled = props.item.disabled
      const checked = mergedChecked.value
      const inGroup = groupCtx.inGroup
      const ctrlType = mergedControlType.value
      const isGroupSelection = Boolean(inGroup && ctrlType)

      const borderColor = checked
        ? (disabled ? colorVars.brandDisabled : colorVars.brand)
        : (hovered.value && interactive.value && !disabled ? colorVars.borderHover : colorVars.border)

      const background = checked && !disabled && !inGroup ? colorVars.brandLight : colorVars.surface

      const showGroupSelBorder = isGroupSelection && (checked || hovered.value || disabled)
      const borderCss = isGroupSelection
        ? (showGroupSelBorder ? `1px solid ${borderColor}` : '1px solid transparent')
        : (mergedBorder.value !== false ? `1px solid ${borderColor}` : 'none')

      const sz = mergedSize.value
      return {
        display: mergedEqualHeight.value ? 'flex' : 'inline-flex',
        flexDirection: 'column',
       gap: `${CARD_INTERNAL_GAP}px`,
        width: mergedEqualHeight.value ? '100%' : 'fit-content',
        maxWidth: '100%',
        minWidth: `${CARD_MIN_WIDTH[sz]}px`,
        minHeight: `${CARD_HEIGHT[sz]}px`,
        ...(mergedEqualHeight.value ? { alignSelf: 'stretch' } : null),
        padding: sz === 'large' ? '12px 16px' : '8px 12px',
        borderRadius: colorVars.radiusCard,
        boxSizing: 'border-box',
        border: borderCss,
        background,
        cursor: interactive.value && !disabled ? 'pointer' : (disabled ? 'not-allowed' : 'default'),
        transition: 'border-color .2s ease, box-shadow .2s ease, background-color .2s ease',
        position: 'relative',
      }
    })

    const rightLayoutStyle: CSSProperties = {
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'space-between',
      gap: `${CARD_RIGHT_PLACEMENT_GAP}px`,
    }

    const rightLeftColStyle: CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: `${CARD_INTERNAL_GAP}px`,
      flex: 1,
      minWidth: 0,
    }

    // --- click handler ---
    const handleClick = () => {
      if (props.item.disabled) return
      if (selectionCtx && mergedControlType.value && !selectionDisabled.value) {
        selectionCtx.toggle(props.item.id)
      }
      emit('click', props.item)
    }

    return {
      cls,
      rootRef,
      hovered,
      mergedSize,
      mergedNumberScroll,
      mergedSubIndicatorsLayout,
      effectiveReverseColor,
      mergedControlType,
      mergedChecked,
      resolvedHeader,
      resolvedChart,
      resolvedExtra,
      cardStyle,
      rightLayoutStyle,
      rightLeftColStyle,
      handleClick,
    }
  },
})
</script>
